<template>
  <LayoutClassicTour>
    <!-- Shared by today's layout and the join box variant's. -->
    <DefineSceneMedia v-slot="{ inset, fill }">
      <figure
        v-if="inAgeRestrictedLocation"
        class="w-full flex flex-col items-center"
      >
        <!-- Most scenes have no SFW poster: show the locked "join now"
             image rather than an empty box. -->
        <a v-if="showLockedPoster" :href="joinUrl" class="w-full">
          <img
            src="/images/locked-scene.webp"
            :alt="`Join now to watch ${release.title}`"
            width="970"
            height="545"
            fetchpriority="high"
            class="w-full max-h-[600px] aspect-video object-cover"
          />
        </a>
        <!-- The SFW poster stands in for the trailer, so it looks like
             one: a play button and a "locked" line over it, linking to
             join. -->
        <a
          v-else
          :href="joinUrl"
          class="group relative block"
          :class="{ 'w-full': fill }"
          :aria-label="`Join today to unlock ${release.title}`"
        >
          <img
            ref="posterEl"
            :src="release.posterUrl"
            :alt="posterAlt"
            width="970"
            height="545"
            fetchpriority="high"
            class="max-h-[600px] object-cover"
            :class="{ 'w-full aspect-video': fill }"
            @error="posterFailed = true"
          />
          <span
            class="absolute inset-0 flex flex-col items-center justify-center gap-3 sm:gap-5 bg-black/35 transition-colors group-hover:bg-black/45"
          >
            <span
              class="flex items-center justify-center w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-black/60 ring-4 ring-white/90 shadow-lg transition-transform group-hover:scale-110"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                class="w-8 h-8 sm:w-12 sm:h-12 ml-1 fill-white"
              >
                <path
                  d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14Z"
                />
              </svg>
            </span>
            <span
              class="flex flex-col items-center gap-1 px-4 py-2 sm:px-6 sm:py-3 rounded-lg bg-black/70 text-center"
            >
              <span
                class="flex items-center gap-2 text-lg sm:text-2xl font-black uppercase text-white"
              >
                <svg
                  viewBox="0 0 24 24"
                  class="w-5 h-5 sm:w-6 sm:h-6 fill-current"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm-3 8V7a3 3 0 1 1 6 0v3H9Z"
                  />
                </svg>
                This video is locked
              </span>
              <span class="text-sm sm:text-lg font-semibold text-[#29f863]">
                Join today to unlock it
              </span>
            </span>
          </span>
        </a>
      </figure>
      <!-- The player is client-only; the poster stands in for it in the
           server-rendered HTML so crawlers and first paint get an image,
           and stays on screen sizes the site doesn't play trailers on. -->
      <ClientOnly v-else>
        <ReleasePlayer
          v-if="trailersAllowed"
          :release="release"
          video-type="trailer"
          :class="inset"
        />
        <figure v-else :class="inset">
          <img
            :src="release.posterUrl"
            :alt="posterAlt"
            width="970"
            height="545"
            fetchpriority="high"
            class="w-full aspect-video object-cover"
          />
        </figure>
        <template #fallback>
          <figure :class="inset">
            <img
              :src="release.posterUrl"
              :alt="posterAlt"
              width="970"
              height="545"
              fetchpriority="high"
              class="w-full aspect-video object-cover"
            />
          </figure>
        </template>
      </ClientOnly>
    </DefineSceneMedia>
    <DefineSceneHeading v-slot="{ large }">
      <div>
        <h1
          class="font-bold leading-none"
          :class="large ? 'text-4xl' : 'text-2xl'"
        >
          {{ release.title }}
        </h1>
        <p v-if="release.actors?.length">
          <span class="font-semi-bold text-green-600">Starring: &nbsp;</span>
          <span v-for="(actor, i) in release.actors" :key="`actor-${i}`">
            <NuxtLink
              :to="`/models/${actor.cached_slug}`"
              class="inline text-lg"
            >
              {{ actor.name }}
            </NuxtLink>
            <span v-if="i < release.actors.length - 1">,&nbsp;</span>
          </span>
        </p>
        <p
          class="mt-1 flex flex-wrap items-center gap-x-1 text-sm text-gray-400"
        >
          <time v-if="releaseDateIso" :datetime="releaseDateIso">
            {{ releaseDateLabel }}
          </time>
          <template v-if="runtimeIso">
            <span v-if="releaseDateIso" aria-hidden="true">·</span>
            <time :datetime="runtimeIso">{{ runtimeMinutes }} min</time>
          </template>
          <template v-if="qualityLabels.length">
            <span aria-hidden="true">·</span>
            <span>{{ qualityLabels.join(" / ") }}</span>
          </template>
          <template v-if="rating">
            <span aria-hidden="true">·</span>
            <span>
              Rated {{ rating }} / 10
              <template v-if="release.voteCount">
                ({{ release.voteCount.toLocaleString("en-US") }}
                {{ release.voteCount === 1 ? "vote" : "votes" }})
              </template>
            </span>
          </template>
          <span
            v-for="badge in badges"
            :key="`badge-${badge}`"
            class="ml-1 px-1 border border-gray-500 rounded text-xs font-bold text-gray-200"
          >
            {{ badge }}
          </span>
        </p>
      </div>
    </DefineSceneHeading>
    <DefineSceneThumbs v-slot="{ inset }">
      <!-- Age-restricted regions get the scene's SFW stills from the API,
           and none at all when it has no SFW stills. -->
      <div
        class="hidden w-full md:flex flex-row items-center justify-between gap-1"
        :class="inset"
      >
        <a
          v-for="(thumb, i) in release?.thumbUrls"
          :key="`release-thumb-${i}`"
          class="w-full"
          :href="joinUrl"
        >
          <NuxtImg
            :src="thumb"
            :alt="galleryAlt(i)"
            class="w-full max-h-[700px] flex-1 object-cover"
            loading="lazy"
          />
        </a>
      </div>
    </DefineSceneThumbs>

    <div v-if="releaseStatus === 'success' && release" class="w-full">
      <nav
        aria-label="Breadcrumb"
        class="w-full max-w-7xl mx-auto px-3 xl:px-0 mt-4 text-sm text-gray-400"
      >
        <ol class="flex flex-wrap items-center gap-1">
          <li>
            <NuxtLink to="/" class="hover:underline">
              {{ currentSite.name }}
            </NuxtLink>
          </li>
          <li aria-hidden="true">›</li>
          <li aria-current="page" class="text-gray-200">
            {{ release.title }}
          </li>
        </ol>
      </nav>

      <!-- The join box variant: the form gets a column of its own, and the
           scene's details move under the player. Below lg everything stacks,
           with the form straight after the player. -->
      <div
        v-if="joinBoxVariant === 'box'"
        class="w-full max-w-7xl mx-auto px-3 xl:px-0 my-4 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(340px,400px)] lg:items-start lg:gap-8"
      >
        <div class="contents lg:flex lg:flex-col lg:gap-5 lg:min-w-0">
          <div class="relative order-1">
            <ReuseSceneMedia :fill="true" />
            <ClientOnly>
              <TourClassicTrailerPlayCountHeader
                v-if="trailersAllowed"
                compact
                class="absolute top-3 left-3 pointer-events-none"
              />
            </ClientOnly>
          </div>

          <div
            class="order-3 flex flex-col md:flex-row md:items-start md:justify-between gap-4"
          >
            <ReuseSceneHeading :large="true" />
            <dl
              v-if="release.ratingsDecimal > 0 || release.rankSite > 0"
              class="flex gap-2 shrink-0"
            >
              <div
                v-if="release.ratingsDecimal > 0"
                class="flex flex-col-reverse items-center min-w-24 px-4 py-2 rounded-lg border border-gray-800 bg-white/5"
              >
                <dt class="text-sm uppercase tracking-wide text-gray-400">
                  Score
                </dt>
                <dd class="text-3xl font-bold">
                  {{ release.ratingsDecimal.toFixed(1) }}
                </dd>
              </div>
              <div
                v-if="release.rankSite > 0"
                class="flex flex-col-reverse items-center min-w-24 px-4 py-2 rounded-lg border border-gray-800 bg-white/5"
              >
                <dt class="text-sm uppercase tracking-wide text-gray-400">
                  Ranking
                </dt>
                <dd class="text-3xl font-bold">#{{ release.rankSite }}</dd>
              </div>
            </dl>
          </div>

          <div class="order-4 flex flex-col gap-2">
            <TourClassicVideoFormats :release="release" chips />
            <p v-if="availabilityText" class="text-sm text-gray-400">
              {{ availabilityText }}
            </p>
          </div>

          <ReuseSceneThumbs inset="order-5" />
        </div>

        <div class="order-2 lg:col-start-2 lg:row-start-1">
          <TourClassicJoinBox active>
            <AppCtaButton
              class="w-full text-center text-2xl tracking-wide uppercase"
              size="xl"
              :href="joinUrl"
            >
              Click here to join now
            </AppCtaButton>
          </TourClassicJoinBox>
        </div>
      </div>

      <div
        v-else
        class="w-full max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-4 my-4"
      >
        <div class="w-full flex flex-col gap-4">
          <ClientOnly>
            <TourClassicTrailerPlayCountHeader
              v-if="trailersAllowed"
              class="mx-3 md:mr-0 md:ml-3 xl:ml-0"
            />
          </ClientOnly>

          <ReuseSceneMedia inset="md:ml-3 md:pr-3 xl:ml-0 xl:pr-0" />

          <ReuseSceneThumbs inset="px-3 md:pr-0 md:pl-3 xl:pl-0" />
        </div>

        <div class="w-full md:w-1/4 flex flex-col px-3 xl:px-0">
          <ReuseSceneHeading />
          <AppCtaButton
            class="my-4 text-center text-2xl tracking-wide uppercase"
            size="xl"
            :href="joinUrl"
          >
            Click here to join now
          </AppCtaButton>

          <div
            v-if="release.ratingsDecimal > 0 || release.rankSite > 0"
            class="w-full flex flex-row items-center border border-gray-700 rounded-md my-2"
          >
            <div
              v-if="release.ratingsDecimal > 0"
              class="w-full flex flex-col items-center px-2 py-4"
            >
              <span class="text-2xl font-bold">
                {{ (release.ratingsDecimal ?? 0).toFixed(1) }}
              </span>
              <span class="uppercase font-thin">Score</span>
            </div>

            <AppDivider class="h-full" orientation="vertical" />

            <div
              v-if="release.rankSite > 0"
              class="w-full flex flex-col items-center"
            >
              <span class="text-2xl font-bold">
                {{ release.rankSite ?? 0 }}
              </span>
              <span class="uppercase font-thin">Ranking</span>
            </div>
          </div>

          <TourClassicVideoFormats :release="release" />
          <p v-if="availabilityText" class="mt-2 text-sm text-gray-400">
            {{ availabilityText }}
          </p>
        </div>
      </div>

      <div
        class="w-full flex md:hidden flex-col items-center justify-between gap-1"
      >
        <a
          v-for="(thumb, i) in release?.thumbUrls"
          :key="`release-thumb-${i}`"
          class="w-full"
          :href="joinUrl"
        >
          <NuxtImg
            :src="thumb"
            :alt="galleryAlt(i)"
            class="w-full"
            loading="lazy"
          />
        </a>
      </div>

      <SectionContainer id="description">
        <template #header>
          <h2 class="text-2xl">About this scene</h2>
        </template>
        <div class="flex flex-col gap-2 max-w-4xl">
          <p v-if="editorialDescription" class="whitespace-pre-line">
            {{ editorialDescription }}
          </p>
          <p class="text-gray-300">{{ sceneSummary }}</p>
        </div>
      </SectionContainer>

      <SectionContainer v-if="sceneTags.length" id="tags">
        <template #header>
          <h2 class="text-2xl">Tags</h2>
        </template>
        <!-- Tags with a hub page link to it; the rest stay plain text. -->
        <ul class="flex flex-wrap gap-2">
          <li v-for="tag in sceneTags" :key="`tag-${tag.slug}`">
            <NuxtLink
              v-if="tag.hasPage"
              :to="`/tags/${tag.slug}`"
              class="block px-2 py-1 border border-gray-700 rounded text-sm hover:border-gray-400"
            >
              {{ tag.label }}
            </NuxtLink>
            <span
              v-else
              class="block px-2 py-1 border border-gray-700 rounded text-sm capitalize"
            >
              {{ tag.label }}
            </span>
          </li>
        </ul>
      </SectionContainer>

      <SectionContainer v-if="performers.length" id="performers">
        <template #header>
          <h2 class="text-2xl">Starring</h2>
        </template>
        <div class="grid gap-2" :class="performerGridClass">
          <ActorCard
            v-for="performer in performers"
            :key="`performer-${performer.cachedSlug}`"
            :actor="performer"
          />
        </div>
      </SectionContainer>

      <SectionContainer v-if="seriesReleases.length" id="series">
        <template #header>
          <div
            class="w-full flex flex-row items-baseline justify-between gap-2"
          >
            <h2 class="text-2xl">More from {{ release.sponsor.name }}</h2>
            <NuxtLink :to="seriesPath" class="text-sm hover:underline">
              All {{ release.sponsor.name }} scenes ›
            </NuxtLink>
          </div>
        </template>
        <div class="card-grid">
          <ReleaseCard
            v-for="seriesRelease in seriesReleases"
            :key="`series-${seriesRelease.id}`"
            :release="seriesRelease"
            :vertical="useVerticalCards"
          />
        </div>
      </SectionContainer>
    </div>

    <Divider />

    <SectionContainer v-if="relatedReleases.length" id="related" class="mt-1">
      <template #header>
        <h2 class="text-2xl">More scenes like this</h2>
      </template>
      <div class="card-grid">
        <ReleaseCard
          v-for="relatedRelease in relatedReleases"
          :key="`related-${relatedRelease.id}`"
          :release="relatedRelease"
          :vertical="useVerticalCards"
        />
      </div>
    </SectionContainer>
  </LayoutClassicTour>
</template>

<script setup>
  import { createReusableTemplate } from "@vueuse/core"

  useRedirectInAgeRestrictedArea()

  const [DefineSceneMedia, ReuseSceneMedia] = createReusableTemplate()
  const [DefineSceneHeading, ReuseSceneHeading] = createReusableTemplate()
  const [DefineSceneThumbs, ReuseSceneThumbs] = createReusableTemplate()

  const currentSite = inject("currentSite")
  const route = useRoute()
  const inAgeRestrictedLocation = inject("inAgeRestrictedLocation")
  const {
    notFoundUrl,
    useVerticalCards,
    useVerticalActorCards,
    hasExclusiveSeries,
    hasSubSites,
  } = useSiteSettings(currentSite.value)
  const joinUrl = "/join"
  const { trailersAllowed } = useTourTrailers()
  const { variant: joinBoxVariant, assign: assignJoinBoxVariant } =
    useJoinBoxVariant()
  const { trackVideoView, trackJoinClick } = useTourEvents()
  const relatedCount = 6

  const [
    { data: release, status: releaseStatus },
    { data: topRatedReleases },
    { data: tagPages },
  ] = await Promise.all([
    useApi(`/api/releases/${route.params.slug}`, {
      query: {
        age_restricted: inAgeRestrictedLocation,
      },
    }),
    // One extra, since the current scene may be in the list.
    useApi("/api/releases", {
      query: {
        sort: "top_rated",
        per_page: relatedCount + 1,
        age_restricted: inAgeRestrictedLocation,
      },
    }),
    // The site's tags that have a hub page, to link the scene's tags.
    useApi("/api/tags"),
  ])

  onServerPrefetch(async () => {
    if (!release.value) {
      await navigateTo(notFoundUrl, { external: true, redirectCode: 302 })
    }
  })

  // Performer cards and related scenes both hang off the cast.
  const actorSlugs = (release.value?.actors ?? [])
    .map((actor) => actor.cached_slug)
    .filter(Boolean)
    .slice(0, 4)

  // The series block. Age-restricted regions get its SFW thumbs, or the
  // locked "join now" card for scenes without one.
  const sponsor = release.value?.sponsor
  const showSeries = seriesBlockCandidate(currentSite.value, sponsor, {
    exclusiveOnly: hasExclusiveSeries,
  })
  const seriesPath = showSeries
    ? seriesHubPath(sponsor.cachedSlug, { hasSubSites })
    : null

  const [performerResponses, relatedResponses, seriesResponse] =
    await Promise.all([
      Promise.all(
        actorSlugs.map((slug) =>
          useApi(`/api/actors/${slug}`, {
            query: { age_restricted: inAgeRestrictedLocation },
          }),
        ),
      ),
      Promise.all(
        actorSlugs.slice(0, 2).map((slug) =>
          useApi(`/api/actors/${slug}/related_releases`, {
            query: { age_restricted: inAgeRestrictedLocation },
          }),
        ),
      ),
      // One extra, since the current scene is in the list.
      showSeries
        ? useApi(`/api/sponsors/${encodeURIComponent(sponsor.cachedSlug)}`, {
            query: {
              per_page: relatedCount + 1,
              age_restricted: inAgeRestrictedLocation,
            },
          })
        : { data: ref(null) },
    ])

  const performers = computed(() =>
    performerResponses.map(({ data }) => data.value).filter(Boolean),
  )

  // Usually one or two performers, so keep the cards smaller than the
  // three-across scene grid.
  const performerGridClass = useVerticalActorCards
    ? "grid-cols-2 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6"
    : "grid-cols-2 md:grid-cols-4"

  // Scenes sharing a performer first, topped up with the site's top rated.
  // related_releases spans the whole network, and other sites' scenes render
  // as offsite cards with no link, so keep this site's only. It also ignores
  // its releases_to_exclude param, so the current scene is filtered out here.
  const relatedReleases = computed(() => {
    const seen = new Set([release.value?.id])
    const picks = []
    const candidates = [
      ...relatedResponses
        .flatMap(({ data }) => data.value ?? [])
        .filter((candidate) => candidate.siteId === currentSite.value.id),
      ...(topRatedReleases.value?.items ?? []),
    ]

    for (const candidate of candidates) {
      if (picks.length >= relatedCount) break
      if (!candidate || seen.has(candidate.id)) continue

      seen.add(candidate.id)
      picks.push(candidate)
    }

    return picks
  })

  // Empty unless the series has enough other scenes on this site to be worth
  // a block (see SERIES_BLOCK_MIN_OTHERS).
  const seriesReleases = computed(() => {
    const releases = seriesResponse.data.value?.releases
    const others = (releases?.pagination?.totalItems ?? 0) - 1
    if (others < SERIES_BLOCK_MIN_OTHERS) return []

    return (releases?.items ?? [])
      .filter((candidate) => candidate.id !== release.value?.id)
      .slice(0, relatedCount)
  })

  const actorNames = computed(() =>
    (release.value?.actors ?? []).map((actor) => actor.name).join(", "),
  )

  const posterAlt = computed(() => {
    const parts = [release.value?.title, actorNames.value].filter(Boolean)
    return `${parts.join(" – ")} on ${currentSite.value.name}`
  })

  // An error that fired before hydration had no listener yet, so check too.
  const posterFailed = ref(false)
  const posterEl = useTemplateRef("posterEl")
  onMounted(() => {
    const img = posterEl.value
    if (img?.complete && img.naturalWidth === 0) posterFailed.value = true
  })
  const showLockedPoster = computed(
    () => posterFailed.value || !isUsableImageUrl(release.value?.posterUrl),
  )

  // At most one join click per view. A click on any /join link counts (CTA,
  // thumbnails, navigation); the join box submits a form instead, so it
  // reports through the injected trackJoinClick.
  let joinClickTracked = false
  const reportJoinClick = () => {
    if (joinClickTracked || !release.value) return
    joinClickTracked = true
    trackJoinClick(release.value)
  }
  provide("trackJoinClick", reportJoinClick)

  const onDocumentClick = (event) => {
    const link = event.target.closest?.("a[href]")
    if (link && new URL(link.href).pathname === joinUrl) reportJoinClick()
  }
  onMounted(() => {
    assignJoinBoxVariant()
    if (release.value) trackVideoView(release.value)
    document.addEventListener("click", onDocumentClick, true)
  })
  onBeforeUnmount(() =>
    document.removeEventListener("click", onDocumentClick, true),
  )

  const galleryAlt = (index) => {
    const count = release.value?.thumbUrls?.length ?? 0
    const photo = `${release.value?.title} – photo ${index + 1} of ${count}`
    return actorNames.value ? `${photo} – ${actorNames.value}` : photo
  }

  const releaseDateIso = computed(() => {
    const date = new Date(release.value?.releasedAt)
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10)
  })

  const releaseDateLabel = computed(() =>
    formatReleaseDate(release.value?.releasedAt),
  )

  const runtimeMinutes = computed(() =>
    durationMinutes(release.value?.durationSeconds),
  )
  const runtimeIso = computed(() => isoDuration(release.value?.durationSeconds))

  // Download heights, best first: [2160, 1080, 720, 480].
  const qualities = computed(() =>
    [
      ...new Set(
        tourDownloadOptions(release.value?.downloadOptions)
          .map((option) => parseInt(option.quality))
          .filter((height) => height > 0),
      ),
    ].sort((a, b) => b - a),
  )

  const qualityLabel = (height) => (height >= 2160 ? "4K" : `${height}p`)

  const qualityLabels = computed(() => qualities.value.map(qualityLabel))

  const availabilityText = computed(() => {
    if (!qualities.value.length) return ""

    const labels = qualities.value.map((height) =>
      height >= 2160 ? `4K (${height}p)` : `${height}p`,
    )
    const formats = [
      ...new Set(
        tourDownloadOptions(release.value?.downloadOptions)
          .map((option) => option.format?.toUpperCase())
          .filter(Boolean),
      ),
    ]

    return `Available in ${joinList(labels)}${formats.length ? ` ${formats.join("/")}` : ""}.`
  })

  const badges = computed(() => {
    const list = []
    if (release.value?.isVr) list.push("VR")
    if (qualities.value[0] >= 2160) list.push("4K")
    if (qualities.value[0] >= 720) list.push("HD")
    return list
  })

  const rating = computed(() =>
    release.value?.ratingsDecimal > 0
      ? release.value.ratingsDecimal.toFixed(1)
      : null,
  )

  const sceneTags = computed(() => {
    const pages = new Map((tagPages.value ?? []).map((tag) => [tag.slug, tag]))

    return (release.value?.tags ?? []).map((slug) => ({
      slug,
      label: pages.get(slug)?.label ?? slug.replace(/[_-]+/g, " "),
      hasPage: pages.has(slug),
    }))
  })

  // The tags in lowercase for prose and metadata, from the tag's own label
  // where it has a page (teen → "teen 18+").
  const tagLabels = computed(() =>
    sceneTags.value.map((tag) => tag.label.toLowerCase()),
  )

  // Descriptions are stored as plain text today; strip any markup rather than
  // rendering it with v-html.
  const editorialDescription = computed(() =>
    (release.value?.description ?? "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .trim(),
  )

  // Names the cast, site, date, quality and tags in body copy. Stands in for
  // the description when there isn't one, and adds the facts when there is.
  const sceneSummary = computed(() => {
    const names = (release.value?.actors ?? []).map((actor) => actor.name)
    const title = release.value?.title
    const site = currentSite.value.name

    const subject = names.length
      ? `${joinList(names)} ${names.length > 1 ? "star" : "stars"} in ${title}, ${withIndefiniteArticle(site)} scene`
      : `${title} is ${withIndefiniteArticle(site)} scene`
    const details = [
      releaseDateLabel.value && `released ${releaseDateLabel.value}`,
      qualities.value.length &&
        `available in ${qualityLabel(qualities.value[0])}`,
    ].filter(Boolean)
    const tags = tagLabels.value.length
      ? ` Tags: ${tagLabels.value.join(", ")}.`
      : ""

    return `${subject}${details.length ? ` ${details.join(" and ")}` : ""}.${tags}`
  })

  if (release.value) {
    // Sites with their own niche and quality words get a description built
    // from them; the rest keep the generic one.
    const copy = siteCopyFor(currentSite.value)
    const seoSite = currentSite.value.alias ?? currentSite.value

    useSiteSeo(
      {
        actorNames: actorNames.value,
        // Scene title first (it's the unique string), performers second
        // (they're the search term), brand last. Sections drop the separators
        // when a release has no actors or tags.
        defaultTitle:
          "{{{ releaseTitle }}}{{#actorNames}} – {{{ actorNames }}}{{/actorNames}} | {{{ siteName }}}",
        // Must name the scene; generic copy reads as duplicate across pages.
        defaultDescription: copy
          ? "{{{ sceneDescription }}}"
          : "{{#actorNames}}Watch {{{ actorNames }}} in{{/actorNames}}{{^actorNames}}Watch{{/actorNames}} {{{ releaseTitle }}} on {{{ siteName }}}.{{#releaseDate}} Released {{ releaseDate }}.{{/releaseDate}}{{#tagList}} Tags: {{ tagList }}.{{/tagList}}",
        sceneDescription:
          copy &&
          sceneDescription({
            copy,
            siteName: seoSite.seoSiteName,
            title: release.value.title,
            actorNames: (release.value.actors ?? []).map((actor) => actor.name),
            releaseDate: formatReleaseDate(release.value.releasedAt),
            durationMinutes: runtimeMinutes.value,
          }),
        releaseTitle: release.value.title,
        releaseDate: formatReleaseDate(release.value.releasedAt),
        tagList: tagLabels.value.slice(0, 5).join(", "),
      },
      {
        // NOTE: no og:video — trailer URLs are CloudFront-signed and expire, so
        // a crawler would only ever cache a dead link.
        type: "video.other",
        image: release.value.posterUrl,
        imageAlt: posterAlt.value,
        imageWidth: 970,
        imageHeight: 545,
        releaseDate: release.value.releasedAt,
      },
      // Scene titles follow the SEO guide in code, not per-site templates.
      { siteTemplates: false },
    )
  }

  useSceneSchema(release, {
    description: () => editorialDescription.value || sceneSummary.value,
    tagLabels,
  })

  function formatReleaseDate(value) {
    if (!value) return ""

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    })
  }
</script>
